import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const CACHE_KEY = "accel_investors_with_firms_v1";

const loadCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.data) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const saveCache = (data, lastUpdated) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data: data || [], lastUpdated })
    );
  } catch (e) {
    // ignore quota / serialization errors
  }
};

const cached = loadCache();

const initialState = {
  data: cached?.data || [],
  liked: [],
  requestConnection: [],
  error: null,
  // Only block the UI with a spinner on a true cold start (no cached data).
  isLoading: false,
  // Background refresh in progress while cached data is already on screen.
  isRefreshing: false,
  lastUpdated: cached?.lastUpdated || null,
};
const reducerPromiseState = {
  pending: (state) => {
    state.isLoading = true;
    state.error = null;
  },
  fulfilled: (state) => {
    state.isLoading = false;
    state.error = null;
  },
  rejected: (state, action) => {
    state.isLoading = false;
    state.error = action.payload;
  },
};

export const getInvestorsWithFirms = createAsyncThunk(
  "investorsWithFirms/getInvestorsWithFirms",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        "/api/investment-firms/getFirmsWithInvestors",

        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
export const setInvestorLiked = createAsyncThunk(
  "investorsWithFirms/setInvestorLiked",
  async ({ investorId, liked = false }, { getState, rejectWithValue }) => {
    try {
      const { investorsWithFirms } = getState();
      let updatedData = [...investorsWithFirms.liked];
      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `/api/investors/${
          liked ? "un" : ""
        }like`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify({
          investorId: investorId,
        }),
      };

      const response = await axios.request(config);

      if (response.data.message) {
        if (liked) {
          updatedData = updatedData.filter((id) => id !== investorId);
        } else {
          updatedData = [...updatedData, investorId];
        }
        return updatedData;
      } else {
        return rejectWithValue("Unexpected response from server");
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const setInvestorRequestConnection = createAsyncThunk(
  "investorsWithFirms/setInvestorRequestConnection",
  async ({ investorId }, { getState, rejectWithValue }) => {
    try {
      const { investorsWithFirms } = getState();
      let updatedData = [...investorsWithFirms.requestConnection];
      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `/api/introductions/request`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify({
          investorId: investorId,
        }),
      };

      const response = await axios.request(config);

      if (response.data.message) {
        updatedData = [...updatedData, investorId];

        return updatedData;
      } else {
        return rejectWithValue("Unexpected response from server");
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export default createSlice({
  name: "investorsWithFirms",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    let builderMap = {
      getInvestorsWithFirms: {
        pending: (state) => {
          state.error = null;
          // If we already have (cached) data on screen, refresh quietly in the
          // background instead of flashing a full-page loading state.
          if (state.data && state.data.length > 0) {
            state.isRefreshing = true;
          } else {
            state.isLoading = true;
          }
        },
        fulfilled: (state, action) => {
          state.isLoading = false;
          state.isRefreshing = false;
          state.data = action.payload?.firmsWithInvestors || [];
          state.requestConnection = action.payload?.userIntroductions || [];
          state.liked = action.payload?.userLikes || [];
          state.lastUpdated = Date.now();
          saveCache(state.data, state.lastUpdated);
        },
        rejected: (state, action) => {
          // Keep showing cached data on a failed background refresh.
          state.isLoading = false;
          state.isRefreshing = false;
          state.error = action.payload;
        },
      },
      setInvestorLiked: {
        fulfilled: (state, action) => {
          state.isLoading = false;
          state.liked = action.payload;
        },
      },
      setInvestorRequestConnection: {
        fulfilled: (state, action) => {
          state.isLoading = false;
          state.requestConnection = action.payload;
        },
      },
    };
    [
      "getInvestorsWithFirms",
      "setInvestorLiked",
      "setInvestorRequestConnection",
    ].forEach((func) => {
      return ["pending", "fulfilled", "rejected"].map((promiseState) => {
        builder.addCase(
          eval(func)[promiseState],
          builderMap?.[func]?.[promiseState] ||
            reducerPromiseState[promiseState]
        );
      });
    });
  },
}).reducer;
